import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import SEO from "@/components/SEO";
import { ArrowRight, CheckCircle2 } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", company: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters.");
    setBusy(true);
    try {
      await signup(form);
      toast.success("Trial started. Welcome to SerpNex.");
      nav("/app/overview");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <SEO pageKey="signup" title="Start Free — SerpNex" description="Start free with SerpNex. Free plan for 5 keywords or a 7-day trial on any paid plan. No credit card. Cancel anytime." canonical="/signup" />
      <div className="min-h-screen grid md:grid-cols-2 bg-white">
        <div className="hidden md:flex flex-col justify-between p-14 bg-[#0A0D14] text-white relative overflow-hidden">
          <div className="absolute inset-0 hero-grid opacity-30" style={{ filter: 'invert(1)' }} />
          <Logo variant="dark" size="lg" />
          <div className="relative">
            <div className="eyebrow" style={{ color: '#7EA1FF' }}>Start Free · 5 keywords</div>
            <h1 className="mt-3 h-display text-4xl sm:text-5xl font-bold">Rankings you<br />can actually<br />trust.</h1>
            <ul className="mt-8 space-y-3 text-white/80 text-sm">
              {["Free plan for 5 keywords, forever","7-day free trial on any paid plan","No credit card required","White-label reports from Pro up"].map((t, i) => (
                <li key={i} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#7EA1FF]" /> {t}</li>
              ))}
            </ul>
          </div>
          <div className="text-xs text-white/40 relative">Simple for solo · Strong for agencies · Focused on rank tracking</div>
        </div>

        <div className="flex items-center justify-center p-6 sm:p-14">
          <form onSubmit={submit} className="w-full max-w-sm" data-testid="signup-form">
            <div className="md:hidden mb-8"><Logo /></div>
            <h2 className="text-3xl font-display font-bold text-[#0A0D14]">Create your account.</h2>
            <p className="mt-2 text-sm text-[#525B6A]">Already here? <Link to="/login" className="text-[#0047FF] font-medium">Log in</Link>.</p>

            <div className="mt-8 space-y-4">
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-1 block">Full name</label>
                <input required data-testid="signup-name" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-1 block">Work email</label>
                <input required type="email" data-testid="signup-email" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-1 block">Company (optional)</label>
                <input data-testid="signup-company" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-1 block">Password</label>
                <input required type="password" minLength={6} data-testid="signup-password" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <button disabled={busy} data-testid="signup-submit" className="btn-primary w-full px-4 py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2">
                {busy ? "Creating account…" : (<>Start Free <ArrowRight className="h-4 w-4" /></>)}
              </button>
              <div className="text-xs text-[#8A94A6] text-center">By continuing, you agree to receive occasional product emails from SerpNex.</div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
